export type PageFieldTarget = {
  id: string;
  label: string;
  section: string | null;
  searchText: string;
  scrollTarget: HTMLElement;
  focusTarget: HTMLElement;
};

const HIGHLIGHT_CLASS = "hub-field-search-highlight";
const HIGHLIGHT_MS = 2_400;

const FOCUSABLE_SELECTOR =
  'input:not([type="hidden"]):not([disabled]):not([aria-hidden="true"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not([aria-hidden="true"]), [role="combobox"]:not([disabled]), [contenteditable="true"]:not([aria-hidden="true"])';

function normalizeSearchText(text: string): string {
  return text.trim().replace(/\s+/g, " ").toLowerCase();
}

function cleanLabelText(text: string): string {
  return text.trim().replace(/\s+/g, " ");
}

function labelTextFromElement(label: HTMLElement): string {
  const clone = label.cloneNode(true) as HTMLElement;
  clone
    .querySelectorAll("button, [role='button'], svg, [data-hover-hint-trigger]")
    .forEach((element) => element.remove());
  return cleanLabelText(clone.textContent ?? "");
}

function isVisible(element: HTMLElement): boolean {
  if (element.closest("[aria-hidden='true']")) return false;
  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") return false;
  if (element.getAttribute("aria-hidden") === "true") return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
}

function isFocusable(element: HTMLElement): boolean {
  if (element.matches(":disabled")) return false;
  if (element.getAttribute("tabindex") === "-1") return false;
  return element.matches(FOCUSABLE_SELECTOR);
}

/** Hover-hint glyphs and other auxiliary controls use tabIndex -1; skip them in Ctrl+K field search. */
function excludedFromPageFieldSearch(element: HTMLElement): boolean {
  return element.getAttribute("tabindex") === "-1";
}

function resolveFocusTarget(control: HTMLElement): HTMLElement {
  if (control.matches('[role="combobox"]')) return control;
  if (control.matches("button")) return control;
  if (control.matches("input, select, textarea")) return control;
  const nested = control.querySelector(FOCUSABLE_SELECTOR);
  if (nested instanceof HTMLElement) return nested;
  return control;
}

function findNearbyFocusable(label: HTMLElement, root: HTMLElement): HTMLElement | null {
  const labelRow = label.parentElement;
  if (!labelRow) return null;

  let sibling: Element | null = labelRow.nextElementSibling;
  while (sibling) {
    if (sibling instanceof HTMLElement) {
      if (sibling.matches(FOCUSABLE_SELECTOR) && isVisible(sibling)) return sibling;
      const nested = sibling.querySelector(FOCUSABLE_SELECTOR);
      if (nested instanceof HTMLElement && isVisible(nested)) return nested;
    }
    sibling = sibling.nextElementSibling;
  }

  const container =
    label.closest("div.grid, div.flex, fieldset, form, section, article, li, td") ?? labelRow.parentElement;
  if (!container) return null;

  for (const candidate of container.querySelectorAll(FOCUSABLE_SELECTOR)) {
    if (!(candidate instanceof HTMLElement)) continue;
    if (!isVisible(candidate)) continue;
    if (label.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING) {
      return candidate;
    }
  }

  const scoped = root.querySelectorAll(FOCUSABLE_SELECTOR);
  for (const candidate of scoped) {
    if (!(candidate instanceof HTMLElement)) continue;
    if (!isVisible(candidate)) continue;
    if (label.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING) {
      return candidate;
    }
  }

  return null;
}

function findSectionLabel(element: HTMLElement): string | null {
  let node: HTMLElement | null = element;
  while (node && node.tagName !== "MAIN") {
    const fieldset = node.closest("fieldset");
    if (fieldset) {
      const legend = fieldset.querySelector("legend");
      const legendText = legend ? cleanLabelText(legend.textContent ?? "") : "";
      if (legendText !== "") return legendText;
    }

    const card = node.closest(".rounded-lg.border");
    if (card) {
      const header = card.querySelector(":scope > .border-b");
      const title = header?.querySelector("h1, h2, h3");
      const titleText = title ? cleanLabelText(title.textContent ?? "") : "";
      if (titleText !== "") return titleText;
    }

    const heading: HTMLElement | null = node.closest("h1, h2, h3");
    if (heading && heading !== node) {
      const headingText = cleanLabelText(heading.textContent ?? "");
      if (headingText !== "") return headingText;
    }

    node = node.parentElement;
  }
  return null;
}

function scrollTargetFor(label: HTMLElement | null, control: HTMLElement): HTMLElement {
  if (label) {
    const wrapper = label.closest("div, td, li, fieldset");
    if (wrapper instanceof HTMLElement) return wrapper;
    return label;
  }
  const wrapper = control.closest("div, td, li, label, fieldset");
  if (wrapper instanceof HTMLElement) return wrapper;
  return control;
}

function buildSearchText(label: string, section: string | null): string {
  const parts = [label, section].filter((part): part is string => part != null && part.trim() !== "");
  return normalizeSearchText(parts.join(" "));
}

function fieldRank(target: PageFieldTarget, query: string): number {
  const q = normalizeSearchText(query);
  if (q.length === 0) return 100;
  const label = normalizeSearchText(target.label);
  const section = target.section ? normalizeSearchText(target.section) : "";
  if (label.startsWith(q)) return 0;
  if (label.includes(q)) return 20;
  if (section.includes(q)) return 40;
  if (target.searchText.includes(q)) return 60;
  return 200;
}

export function fieldMatchesQuery(target: PageFieldTarget, query: string): boolean {
  const q = normalizeSearchText(query);
  if (q.length === 0) return true;
  return target.searchText.includes(q);
}

export function comparePageFieldTargets(
  left: PageFieldTarget,
  right: PageFieldTarget,
  query: string,
): number {
  const leftRank = fieldRank(left, query);
  const rightRank = fieldRank(right, query);
  if (leftRank !== rightRank) return leftRank - rightRank;
  const sectionCompare = (left.section ?? "").localeCompare(right.section ?? "", undefined, {
    sensitivity: "base",
  });
  if (sectionCompare !== 0) return sectionCompare;
  return left.label.localeCompare(right.label, undefined, { sensitivity: "base" });
}

export function filterPageFieldTargets(targets: PageFieldTarget[], query: string): PageFieldTarget[] {
  const trimmed = query.trim();
  const filtered = targets.filter((target) => fieldMatchesQuery(target, trimmed));
  return [...filtered].sort((left, right) => comparePageFieldTargets(left, right, trimmed));
}

export function collectPageFieldTargets(root: HTMLElement): PageFieldTarget[] {
  const seenFocus = new Set<HTMLElement>();
  const results: PageFieldTarget[] = [];

  const addTarget = (
    label: string,
    scrollTarget: HTMLElement,
    focusTarget: HTMLElement,
    section: string | null,
  ): void => {
    const cleanLabel = cleanLabelText(label);
    if (cleanLabel.length < 2) return;
    if (excludedFromPageFieldSearch(focusTarget)) return;
    if (!isVisible(scrollTarget) && !isVisible(focusTarget)) return;
    if (seenFocus.has(focusTarget)) return;
    seenFocus.add(focusTarget);
    results.push({
      id: `${cleanLabel}-${String(results.length)}`,
      label: cleanLabel,
      section,
      searchText: buildSearchText(cleanLabel, section),
      scrollTarget,
      focusTarget,
    });
  };

  root.querySelectorAll("label[for]").forEach((labelNode) => {
    if (!(labelNode instanceof HTMLLabelElement)) return;
    const forId = labelNode.htmlFor.trim();
    if (forId === "") return;
    const control = root.querySelector(`#${CSS.escape(forId)}`);
    if (!(control instanceof HTMLElement)) return;
    const focusTarget = resolveFocusTarget(control);
    addTarget(
      labelTextFromElement(labelNode),
      scrollTargetFor(labelNode, focusTarget),
      focusTarget,
      findSectionLabel(labelNode),
    );
  });

  root.querySelectorAll("label:not([for])").forEach((labelNode) => {
    if (!(labelNode instanceof HTMLLabelElement)) return;
    const nestedControl = labelNode.querySelector(FOCUSABLE_SELECTOR);
    if (nestedControl instanceof HTMLElement) {
      const focusTarget = resolveFocusTarget(nestedControl);
      addTarget(
        labelTextFromElement(labelNode),
        scrollTargetFor(labelNode, focusTarget),
        focusTarget,
        findSectionLabel(labelNode),
      );
      return;
    }
    const nearby = findNearbyFocusable(labelNode, root);
    if (!nearby) return;
    const focusTarget = resolveFocusTarget(nearby);
    addTarget(
      labelTextFromElement(labelNode),
      scrollTargetFor(labelNode, focusTarget),
      focusTarget,
      findSectionLabel(labelNode),
    );
  });

  root.querySelectorAll("[data-hub-field-label]").forEach((labelNode) => {
    if (!(labelNode instanceof HTMLElement)) return;
    if (labelNode.matches("label")) return;
    const label = cleanLabelText(labelNode.textContent ?? "");
    const nearby = findNearbyFocusable(labelNode, root);
    const focusTarget = nearby ? resolveFocusTarget(nearby) : null;
    if (!focusTarget) return;
    addTarget(
      label,
      scrollTargetFor(labelNode, focusTarget),
      focusTarget,
      findSectionLabel(labelNode),
    );
  });

  root.querySelectorAll(FOCUSABLE_SELECTOR).forEach((controlNode) => {
    if (!(controlNode instanceof HTMLElement)) return;
    if (seenFocus.has(controlNode)) return;
    const labelledBy = controlNode.getAttribute("aria-labelledby")?.trim();
    if (labelledBy) {
      const labelParts = labelledBy
        .split(/\s+/)
        .map((id) => root.querySelector(`#${CSS.escape(id)}`))
        .filter((node): node is HTMLElement => node instanceof HTMLElement)
        .map((node) => labelTextFromElement(node))
        .filter((text) => text !== "");
      if (labelParts.length > 0) {
        const focusTarget = resolveFocusTarget(controlNode);
        addTarget(
          labelParts.join(" "),
          scrollTargetFor(null, focusTarget),
          focusTarget,
          findSectionLabel(controlNode),
        );
        return;
      }
    }
    const ariaLabel = controlNode.getAttribute("aria-label")?.trim() ?? "";
    if (ariaLabel !== "") {
      const focusTarget = resolveFocusTarget(controlNode);
      addTarget(
        ariaLabel,
        scrollTargetFor(null, focusTarget),
        focusTarget,
        findSectionLabel(controlNode),
      );
    }
  });

  return results;
}

export function jumpToPageField(target: PageFieldTarget): void {
  target.scrollTarget.scrollIntoView({ block: "center", behavior: "smooth" });
  target.scrollTarget.classList.remove(HIGHLIGHT_CLASS);
  void target.scrollTarget.offsetWidth;
  target.scrollTarget.classList.add(HIGHLIGHT_CLASS);
  window.setTimeout(() => {
    target.scrollTarget.classList.remove(HIGHLIGHT_CLASS);
  }, HIGHLIGHT_MS);

  if (isFocusable(target.focusTarget)) {
    window.setTimeout(() => {
      target.focusTarget.focus({ preventScroll: true });
    }, 280);
  }
}

export function isFieldSearchShortcut(event: globalThis.KeyboardEvent): boolean {
  if (event.key !== "k" && event.code !== "KeyK") return false;
  return event.ctrlKey || event.metaKey;
}
